"use client";

import { useCallback, type MouseEvent } from "react";

function focusElement(element: HTMLElement) {
  try {
    element.focus({ preventScroll: true });
  } catch {
    element.focus();
  }
}

function findFirstFocusable(container: Element): HTMLElement | null {
  const candidates = Array.from(
    container.querySelectorAll<HTMLElement>(
      [
        'a[href]:not([tabindex="-1"])',
        'button:not([disabled]):not([tabindex="-1"])',
        'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
        'select:not([disabled]):not([tabindex="-1"])',
        'textarea:not([disabled]):not([tabindex="-1"])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(",")
    )
  );

  return (
    candidates.find(
      (candidate) =>
        candidate.getAttribute("aria-hidden") !== "true" &&
        (candidate.getClientRects().length > 0 || candidate.offsetParent !== null)
    ) ?? null
  );
}

export function SkipToContentLink({
  targetId = "main",
  className,
  label = "Skip to content",
}: {
  targetId?: string;
  className: string;
  label?: string;
}) {
  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (typeof document === "undefined") return;

      const target =
        document.getElementById(targetId) ??
        document.querySelector("main") ??
        document.querySelector('[role="main"]');

      if (!target) return;

      event.preventDefault();

      target.scrollIntoView({ block: "start" });

      if (target instanceof HTMLElement) {
        if (!target.hasAttribute("tabindex")) {
          target.setAttribute("tabindex", "-1");
        }
        focusElement(target);

        if (document.activeElement !== target) {
          const firstFocusable = findFirstFocusable(target);
          if (firstFocusable) {
            focusElement(firstFocusable);
          }
        }
      }

      if (typeof history !== "undefined" && typeof history.replaceState === "function") {
        history.replaceState(null, "", `#${targetId}`);
      }
    },
    [targetId]
  );

  return (
    <a href={`#${targetId}`} className={className} onClick={handleClick}>
      {label}
    </a>
  );
}
